import { Button, Typography } from "@suraasa/placebo-ui"
import { PronunciationFeedbackType } from "api/resources/learningItems/types"
import { AISparkleFilled } from "assets/icons/system/AISparkle"
import clsx from "clsx"
import AsyncBuilder from "components/AsyncBuilder"
import { LightBulb, RefreshDouble } from "iconoir-react"
import Markdown from "markdown-to-jsx"
import { useState } from "react"
import { IS_LOCALHOST } from "utils/constants"
import { getTextGradientStyles } from "utils/helpers"

import { PRONUNCIATION_PASSING_SCORE } from "../constant"
import InaudibleAudioState from "../InaudibleAudioState"
import FakeProgress from "./FakeProgress"
import FeedbackAnalysisSheet from "./FeedbackAnalysisSheet"

const PronunciationFeedback = ({
  onRetry,
  pronunciationFeedback,
  onContinue,
  isLastQuestion,
  isLoading,
  isInaudibleAudio,
}: {
  onRetry: () => void
  pronunciationFeedback?: PronunciationFeedbackType
  onContinue: () => Promise<void>
  isLastQuestion: boolean
  isLoading?: boolean
  isInaudibleAudio?: boolean
}) => {
  const gradient = getTextGradientStyles(
    "linear-gradient(90deg, #6780F3 0%, #485CBA 50%, #1F3390 100%)"
  )
  const hasFailed =
    pronunciationFeedback &&
    pronunciationFeedback.overallClarityScore < PRONUNCIATION_PASSING_SCORE
  const [openAnalysis, setOpenAnalysis] = useState(false)
  const onOpenAnalysisSheet = () => {
    setOpenAnalysis(true)
  }
  const onCloseAnalysisSheet = () => {
    setOpenAnalysis(false)
  }

  if (isInaudibleAudio) {
    return (
      <InaudibleAudioState
        onRetry={() => {
          onRetry()
          document.getElementById("retry-button")?.click()
        }}
      />
    )
  }

  if (!isLoading && !pronunciationFeedback) return null
  return (
    <>
      <div className="flex w-[calc(100%_+_32px)] translate-x-0 translate-y-10 flex-wrap items-start justify-between gap-3 rounded-b-2xl border border-surface-200 bg-surface-100 px-5 py-3">
        {isLoading ? (
          <div className="mx-auto flex max-w-[361px] flex-col items-center gap-1 text-center">
            <div className="w-fit">
              <FakeProgress duration={25} />
              <Typography
                variant="strong"
                className="relative mt-1 flex shrink-0 items-center gap-0.25"
              >
                <AISparkleFilled
                  fill="#579DFB"
                  widths={"24px"}
                  height={"20px"}
                />
                <span style={gradient}>Analysing your speech</span>
              </Typography>
            </div>
            <Typography className="text-onSurface-500">
              This may take a moment. We’re preparing personalized feedback to
              help you improve.
            </Typography>
          </div>
        ) : (
          <>
            <div className="w-full max-w-[385px]">
              <div className="mb-1.5 flex items-center justify-between">
                <Typography variant="smallBody">
                  <Typography
                    variant="title4"
                    className={clsx("inline", {
                      "text-critical-500": hasFailed,
                    })}
                  >
                    Clarity: {pronunciationFeedback?.overallClarityScore}
                  </Typography>{" "}
                  / 100
                </Typography>
                <Button
                  variant="text"
                  onClick={onOpenAnalysisSheet}
                  startAdornment={<LightBulb />}
                >
                  View Analysis
                </Button>
              </div>
              <Typography variant="smallBody">
                <Markdown>
                  {pronunciationFeedback?.overallFeedback || ""}
                </Markdown>
              </Typography>
            </div>
            <AsyncBuilder
              handler={async () => {
                await onContinue()
              }}
              render={({ loading, onClick }) => (
                <div className="flex items-center gap-2">
                  <Button
                    rounded
                    disabled={loading}
                    startAdornment={<RefreshDouble />}
                    color="black"
                    onClick={onRetry}
                  >
                    Retry
                  </Button>

                  {(!hasFailed || IS_LOCALHOST) && (
                    <Button
                      onClick={onClick}
                      rounded
                      loading={loading}
                      className="!bg-success-500 hover:!bg-success-600"
                    >
                      {isLastQuestion ? "Submit" : "Continue"}
                    </Button>
                  )}
                </div>
              )}
            />
          </>
        )}
      </div>
      {pronunciationFeedback && (
        <FeedbackAnalysisSheet
          open={openAnalysis}
          handleClose={onCloseAnalysisSheet}
          pronunciationFeedback={pronunciationFeedback}
        />
      )}
    </>
  )
}

export default PronunciationFeedback
